/**
 * `Ops.diff(from, to)` (W1-03 core-model-ops; ARCHITECTURE.md §5.3): the ops that turn document `from` into
 * document `to`, used for version restore as ONE undoable transaction (`tx.applyOps(Ops.diff(tx.doc, target))`),
 * sidecar re-anchoring and applying a whole re-lifted track.
 *
 * Per record: del (absent in `to`), ins (absent in `from`) or set of exactly the top-level fields that differ;
 * per singleton a `single` op of the differing fields; per order list an `order` op when the lists differ; and, as
 * the contract says, always one idSeq op with max(from.idSeq, to.idSeq) (the counter never goes down, so ids are
 * never reused even when restoring an older version; when `from` is already at the maximum the op changes
 * nothing, and a TxDraft drops it from its result). Deletions come first (dependents before owners), then
 * insertions (owners before dependents), then updates, singletons, order lists and the counter.
 */

import { recordDiff } from './compose';
import { valueEquals } from './equal';
import type { DelOp, InsOp, Op, SetOp } from './types';
import type { ChartDoc, OrderListName, SingletonName, TableName, TableRecords } from '../model/types';

type AnyRec = TableRecords[TableName];

/** Owners before dependents (insert order); deletions use the reverse. */
const INSERT_ORDER: readonly TableName[] = [
  'assets',
  'groups',
  'bpm',
  'markers',
  'lines',
  'effects',
  'videos',
  'tracks',
  'keys',
  'notes',
  'generators',
];

const SINGLETON_ORDER: readonly SingletonName[] = ['meta', 'pkg', 'timing', 'editor', 'rpeRoot'];
const ORDER_LISTS: readonly OrderListName[] = ['lineOrder', 'effectOrder'];

function table(doc: ChartDoc, tbl: TableName): ReadonlyMap<AnyRec['id'], AnyRec> {
  return doc[tbl];
}

export function diffDocs(from: ChartDoc, to: ChartDoc): Op[] {
  const dels: Op[] = [];
  const inss: Op[] = [];
  const sets: Op[] = [];

  for (let i = INSERT_ORDER.length - 1; i >= 0; i--) {
    const tbl = INSERT_ORDER[i] as TableName;
    const a = table(from, tbl);
    const b = table(to, tbl);
    for (const [id, rec] of a) if (!b.has(id)) dels.push({ o: 'del', tbl, rec } as DelOp);
  }
  for (const tbl of INSERT_ORDER) {
    const a = table(from, tbl);
    const b = table(to, tbl);
    for (const [id, rec] of b) {
      const old = a.get(id);
      if (old === undefined) {
        inss.push({ o: 'ins', tbl, rec } as InsOp);
      } else if (old !== rec) {
        const d = recordDiff(old, rec);
        if (Object.keys(d.next).length > 0) sets.push({ o: 'set', tbl, id, prev: d.prev, next: d.next } as SetOp);
      }
    }
  }

  const out: Op[] = [...dels, ...inss, ...sets];
  for (const key of SINGLETON_ORDER) {
    const a = from[key];
    const b = to[key];
    if (a === b) continue;
    const d = recordDiff(a, b);
    if (Object.keys(d.next).length > 0) out.push({ o: 'single', key, prev: d.prev, next: d.next });
  }
  for (const list of ORDER_LISTS) {
    const a = from[list] as readonly number[];
    const b = to[list] as readonly number[];
    if (!valueEquals(a, b)) out.push({ o: 'order', list, prev: a, next: b });
  }
  out.push({ o: 'idSeq', next: Math.max(from.idSeq, to.idSeq) });
  return out;
}
